/**
  * @class msysExec
  * @description
  * Enables execution of scripts - lists of Mosy messages.
  * As a side effect, in debug build also enables mosy.send() method
  * to allow manual message send from browser's development console.
  */

import msysListener from 'msysListener'
const mfs = require('msysFS')
const ms = require('msysMessage')

// not really used in msysExec but provided to be accessible from mmJs scripts
// must remain declared using var !!!
/* eslint-disable no-var, no-unused-vars */
var mgeo = require('msysGeo')
var mcrypt = require('msysCrypt')
var app_type = MOSY_APP+MOSY_HOST
var app_version = MOSY_APP_WITH_VERSION
var mserver_url = ''
/* eslint-enable no-var, no-unused-vars */
const inspector = require('inspector')

export default class msysExec extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger'], dependencies)
    cfg_schema = _.extend({
      start_script: { type: 'string', description: 'commands to be executed after `mmStartedAll` message indicating all listeners were initialized' },
      start_file: { type: 'string', default: '', description: 'file with commands to be executed after `mmStartedAll` message indicating all listeners were initialized' },
      update_dir: { type: 'string', default: 'updates/', description: 'directory where update files are stored' },
      update_uri: { type: 'string', default: 'updates/', description: 'uri where server provides update files' },
      update: { type: 'string', default: '', description: 'update to be performed; identifies JS file that shall be executed; if update file is not found, it will be requested from server' },
      inspect_port: { type: 'integer', description: 'port where inspector is running', default: 9999 },
      inspect_host: { type: 'string', description: 'host where inspector is running', default: '127.0.0.1' },
      inspect_id: { type: 'string', description: 'password for starting the inspector', default: 'KarmaJeZdarma' },
      inspect_timeout: { type: 'integer', description: 'time after which active inspector closes (in seconds)', default: 3600 },
      classes: { type: 'array', description: 'JavaScript classes that can be used to construct listeners in start_listeners', default: [] },
      start_listeners: { type: 'array', description: 'starts defined listeners', default: [] },
    }, cfg_schema)
    cfg_schema.start_file.default = cfg_schema.start_file.default.replace('MOSY_APP', MOSY_APP)
    super(id, cfg, cfg_schema, dependencies)
    this.classes = {}
    this.config.classes.forEach(cls=>{
      this.classes[cls.default.prototype.constructor.name]=cls
    })
    const top = typeof (window)==='undefined' ? global : window
    const mosy = top.mosy = {}
    mosy.lodash = require('lodash')
    mosy.listeners = this._listeners
    mosy.queues = this._queues
    mosy.lang = LANG
    mosy.oid = this.oid.bind(this)
    mosy.msysCommon = require('msysCommon')
    mosy.msysCrypt = require('msysCrypt')
    mosy.msysFS = require('msysFS')
    mosy.msysGeo = require('msysGeo')
    mosy.publish = this.publish.bind(this)
    mosy.publishAs = this.publishAs.bind(this)
    mosy.request = this.request.bind(this)
    mosy.subscribe = this.subscribe.bind(this)
    mosy.subscribeAs = this.subscribeAs.bind(this)
    mosy.unsubscribe = this.unsubscribe.bind(this)
    mosy.unsubscribeAs = this.unsubscribeAs.bind(this)
    mosy.connectionPause = (duration)=>this.publish('#ALL', 'mmConnectionPause', { duration })
    mosy.connectionResume = ()=>this.publish('#ALL', 'mmConnectionResume')
    mosy.serverPause = (duration)=>this.publish('msysDomainClient', 'mmConnectionPause', { duration })
    mosy.serverResume = ()=>this.publish('msysDomainClient', 'mmConnectionResume')
    mosy.peeringPause = (duration)=>this.publish('msysPeeringClient', 'mmConnectionPause', { duration })
    mosy.peeringResume = ()=>this.publish('msysPeeringClient', 'mmConnectionResume')

    if(typeof process !== 'undefined') {
      this.gracefulExit = this.gracefulExit.bind(this)
      process.addListener('SIGINT', this.gracefulExit)
    }
  } // constructor

  gracefulExit() {
    process.removeListener('SIGINT', this.gracefulExit)
    mcrypt.system_shutdown = true
    this.warn('SIGINT received')
    this.publish('#ALLWITHME', 'mmExit')
    setTimeout(()=>{
      process.exit()
    }, 3000)
  } // gracefulExit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  mmStart(sender, params) {
    super.mmStart(sender, params)
    this.loadUpdatePackage()
    this.subscribe('#WWW')
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Loads configured update script from file and executes it.
  */
  async loadUpdatePackage() {
    const update_id = String(this.config.update??'')
    if(update_id==='') return

    // load update script
    const file = this.config.update_dir + update_id+'.js'
    let script = ''
    try{
      script = await mfs.promises.readFile(file)
      if(typeof script !== 'string') {
        this.error(`Invalid update package: ${file}`)
        return
      }
    }catch(e) {
      this.warn(`Cannot load update package: ${file}`)
      return
    }

    this.executeUpdatePackage(script)
  } // loadUpdatePackage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Evaluates configured update script.
  */
  async executeUpdatePackage(script) {
    this.debug('Executing update package')
    if(!/\S/.test(script)) return
    if(this.loaded_update) {
      this.publish('mmodUpdates', 'mmOpen', { view: 'updating' })
      setTimeout(()=>{
        this.publish('#ALLWITHME', 'mmExit')
      }, 5000)
      return
    }

    // perform update script
    // eslint-disable-next-line no-undef
    const webpack_require = __webpack_require__
    const me = this
    try{
      // inject mosy_require function to allow loading of core modules in evaluated script
      // eslint-disable-next-line no-var, no-unused-vars
      const top = typeof (window)==='undefined' ? global : window
      top.mosy_require = function(module, no_error) {
        const m = /^(mele|mmod|msvc|msvr|msys)\w+$/.exec(module)
        const module_path = m ? `./src/${m[1]}/${module}/${module}.js` : module
        try{
          const module = webpack_require(module_path)
          return m ? (module.default || module) : module
        }catch(e) {
          if(!no_error) me.error(`Error require module ${module} path ${module_path}: ${e}`)
        }
      }

      // eslint-disable-next-line no-eval
      eval(script)
      const hash = mcrypt.md5(script, 'utf8')
      mfs.setItem('updatehash', hash)
    }catch(e) {
      this.error(`Error executing update package: ${e}`)
    }

    this.loaded_update = true
    this.debug('Update package executed')
    /* eslint-enable no-var, no-undef, no-unused-vars, no-use-before-define */
  } // executeUpdatePackage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Download configured update script if missing
  */
  async downloadUpdatePackage(server_url) {
    const update_id = String(this.config.update??'')
    if(update_id==='') return

    const file = this.config.update_dir + update_id + '.js'
    let local_hash = null
    // one more check for file presence
    try{
      // check local md5 hash
      const local_copy = await mfs.promises.readFile(file)
      local_hash = mcrypt.md5(local_copy, 'utf8')
      this.debug(`Local update package: (${local_hash})`)
    }catch(e) {
      this.debug(`Local update package not found: ${file}`)
      // ignore
    }

    // download update script
    let script= null
    const url = server_url + this.config.update_uri + this.config.update + '.js'
    // check remote file for hash

    let remote_hash = null
    try {
      remote_hash = await mfs.promises.readFile(url+'.md5')
      this.debug(`Remote update package: (${remote_hash})`)
    } catch (error) {
      // ignore
    }

    if(local_hash && remote_hash && local_hash===remote_hash) {
      this.debug(`Hashes are the same: ${local_hash} === ${remote_hash}. Skipping download.`)
      return
    }

    try{
      this.debug(`Downloading update package: ${url}`)
      script = await mfs.promises.readFile(url)
      const downloaded_hash = mcrypt.md5(script, 'utf8')
      if(local_hash===downloaded_hash) {
        this.debug(`Hashes are the same: ${local_hash} === ${downloaded_hash}. Skipping update.`)
        return
      }
      if(typeof script !== 'string') {
        this.debug(`Invalid update package: ${url}`)
        return
      }
    }catch(e) {
      this.debug(`Cannot download update package: ${e}`)
      return
    }

    // save update script to file
    try{
      await mfs.promises.writeFile(file, script, 'text')
    }catch(e) {
      this.debug(`Error saving update package: ${e}`)
    }

    // perform update script
    this.executeUpdatePackage(script)
  } // downloadUpdatePackage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to execute a list of commands.
  * @param {object} sender - Sender of the message
  * @param {object} params - message parameters
  * @param {string} [params.file] - path to a file to execute
  * @param {string} [params.script] - direct list of commands
  */
  mmExecute(sender, params) {
    if(params.file) {
      mfs.readFile(params.file, (err, data)=>{
        if(err) return this.warn(err)
        this.mmExecute(sender, { script: data })
      })
      return
    }

    // perform script steps
    const lines = _.toString(params.script).split(/\n/)
    this._executeArrayOfMessages(lines)
  } // mmExecute

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Executes list of commands.
  * Supported command syntax is:
  * sender>recipient.action?{parameters}
  * or wait(number_of_miliseconds)
  * @param {string[]} commands
  */
  _executeArrayOfMessages(lines) {
    if(!Array.isArray(lines)) return
    let line = ''
    while(lines.length>0 && String(line??'')==='') {
      line = lines.shift()
        .replace(/^\s*/, '')
        .replace(/\s*$/, '')
        .replace(/^\s*[#;].*/, '')
        .replace(/^\s*\/\/.*/, '')
    }
    if(String(line??'')==='') return

    // wait() command
    const x = line.match(/^\s*(wait|pause|sleep)\((\d+)\)\s*$/)
    if(x!=null) {
      setTimeout(()=>this._executeArrayOfMessages(lines), x[2])
      return
    }

    // send message command
    const msg = ms.string2msg(line)
    if(msg) {
      this.publishAs(msg.sender, msg.target, msg.action, msg.params)
      setTimeout(()=>this._executeArrayOfMessages(lines), 1)
    }
  } // _executeArrayOfMessages

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  mmStartedAll(sender, params) {
    console.logger_worker.info('system', `STARTED APPLICATION ${MOSY_APP_WITH_VERSION}: LOAD=${params.loaded-params.init} START=${params.started-params.loaded} TOTAL=${params.started-params.init}`, new Date())
    if(typeof window==='object' && window?.parent) {
      window.parent.postMessage({ type: 'mosy:started:'+MOSY_APP_WITH_VERSION }, '*')
    }
    this.config.start_listeners.forEach(def=>{
      try{
        let [class_name, listener_id, default_cfg] = typeof (def)==='string' ? [def] : def
        const class_constructor = this.classes[class_name]
        if(typeof (listener_id)!=='string' || String(listener_id??'')==='') listener_id = class_name
        if(!default_cfg || typeof (default_cfg)!=='object') default_cfg = {}
        // eslint-disable-next-line no-new, new-cap
        new class_constructor.default(listener_id, default_cfg)
      }catch(e) {
      }
    })
    if(this.config.start_script) {
      this.publish('#ME', 'mmExecute', { script: this.config.start_script })
    }
    if(this.config.start_file) {
      mfs.readFile(this.config.start_file, (err, script)=>{
        if(!err && /\S/.test(script)) {
          this.publish('#ME', 'mmExecute', { script })
        }
      })
    }
  } // mmStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to execute a javascript.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.script] - direct list of commands
  */
  mmJs(sender, params) {
    if(sender.scope!==':') return // accept command from server only

    // follwing methods are declared for use in evaluated script

    // eslint-disable-next-line no-unused-vars
    function success(result) {
      if(_exec_sender.request_id) {
        _exec_me.reply(_exec_sender, 'mmJsResult', { result })
      }
    }

    // eslint-disable-next-line no-unused-vars
    function failure(error) {
      if(_exec_sender.request_id) {
        _exec_me.reply(_exec_sender, 'mmJsResult', { errcode: 'ERROR', errmsg: _.isObjectLike(error)?error.message:_.toString(error) })
      }
    }

    // eslint-disable-next-line no-unused-vars
    function notify(id, msg, info) {
      const priority = info ? 1 : 11
      const timeout = info ? 3 : undefined
      _exec_me.publish('#NOTIFICATIONS', 'mmNotify', { id, message: msg, priority, timeout })
    }
    try{
      // eslint-disable-next-line no-var
      var _exec_me = this
      // eslint-disable-next-line no-var
      var _exec_sender = sender
      const script = String(params.script??'')
      // eslint-disable-next-line no-eval
      if(script!=='') eval(script)
    }catch(e) {
      if(_exec_sender.request_id) {
        _exec_me.reply(_exec_sender, 'mmJsResult', { errcode: 'EXCEPTION', errmsg: e.message })
      }
    }
  } // mmJs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    if (params.url.startsWith('wss')) {
      mserver_url = params.url.replace(/^wss/, 'https')
    } else if (params.url.startsWith('ws')) {
      mserver_url= params.url.replace(/^ws/, 'http')
    }
    this.downloadUpdatePackage(mserver_url)
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    mserver_url = ''
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysAbstractServer request content for the web script from other listeners
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.ip] - client IP address
  * @param {string} [params.method] - HTTP request method
  * @param {object} [params.headers] - HTTP heders
  * @param {string} params.path - path part of request URL
  * @param {object,array} [params.params] - script parameters parsed from request URL
  * @param {string} [params.hash] - hash part of request URL
  * @param {object} [params.user] - authenticated user
  */
  mmWebRequest(sender, params) {
    if(params.path==='/inspect') {
      const id = String(params.params.id??'')
      const action = params.params.action

      const { inspect_id, inspect_host, inspect_port, inspect_timeout } = this.config

      if(id===inspect_id && id.length>3) {
        // don't allow too small passwords
        if(action==='on') {
          this.error(`inspector.open(${inspect_port}, ${inspect_host});`)
          inspector.open(inspect_port, inspect_host)
          this.inspect_enabled = true
          setTimeout(()=>{
            this.warn(`Disabling inspector after timeout of ${inspect_timeout} seconds`)
            inspector.close()
            this.inspect_enabled = false
          }, inspect_timeout*1000)
        }else if(action==='off') {
          inspector.close()
          this.inspect_enabled = false
        }

        if(this.inspect_enabled) {
          const inspect_url = inspector.url()
          this.warn(`Enabling inspector: ${inspect_url} for ${inspect_timeout} seconds`)
          const content = `
            Inspector is enabled<br>
            ${inspect_url}<br>
            Go to <b>chrome://inspect</b><br>
            <form action="/inspect" method="post">
              <input type=hidden name=id value="${inspect_id}">
              <input type=hidden name=action value="off">
              <input type=submit value="DISABLE">
            </form>`
          this.reply(sender, 'mmWebResponse', { content })
        }else{
          this.warn('Disabling inspector')
          const content = `
            Inspector is disabled<br>
            <form action="/inspect" method="post">
              <input type=hidden name=id value="${inspect_id}">
              <input type=hidden name=action value="on">
              <input type=submit value="ENABLE">
            </form>`
          this.reply(sender, 'mmWebResponse', { content })
        }
      }else{
        const content = `
          Enter password:<br>
          <form action="/inspect" method="post">
            <input type=password name=id>
            <input type=submit value="LOG IN">
          </form>`
        this.reply(sender, 'mmWebResponse', { content })
      }
    }
  } // mmWebRequest

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to get configuration of all listeners
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  async mmGetConfiguration(sender, params) {
    const listeners = Object.keys(this._listeners).filter(x=>!/^\d+$/.test(x)).sort()
    const configs = {}
    const promises = []
    for(const id of listeners) {
      promises.push(this.request(id, 'mmGetConfig', {}, { timeout: 500, resolve: 'mmConfig' }))
    }
    try{
      const results = await Promise.allSettled(promises)
      for(let i=0; i<listeners.length; i++) {
        if(results[i].status==='fulfilled') {
          configs[listeners[i]] = results[i].value.params
        }
      }
      this.reply(sender, 'mmConfiguration', { configs })
    }catch(e) {
      this.reply(sender, 'mmFailure', { error: String(e) })
    }
  } // mmGetConfiguration

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Save application configuration to a file
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.id - ID of configuration
  * @param {object} [params.configs] - configuration to be saved; if not provided, current runtime configuration will be saved
  * @param {array} [params.listeners] - limit for which listeners to save configuration
  */
  async mmSaveConfiguration(sender, params) {
    let { id, configs, listeners } = params
    if(!/^\w+$/.test(id)) {
      this.replyIfRequest(sender, 'mmFailure', { error: 'Invalid configuration id: '+id })
      return
    }

    if(!configs) {
      configs = {}
      const promises = []
      let listener_ids = listeners || Object.keys(this._listeners)
      listener_ids = listener_ids
        .filter(id=>!/^\d+$/.test(id))
        .filter(id=>this._listeners[id]?.status==='started')
        .sort()
      for(const id of listener_ids) {
        promises.push(this.request(id, 'mmGetConfig', { no_mask: true }, { timeout: 500, resolve: 'mmConfig' }))
      }
      try{
        const results = await Promise.allSettled(promises)
        for(let i=0; i<listener_ids.length; i++) {
          if(results[i].status==='fulfilled') {
            configs[listener_ids[i]] = results[i].value.params
          }
        }
      }catch(e) {
        return this.reply(sender, 'mmFailure', { error: String(e) })
      }
    }

    try{
      mfs.setJSON(id+'.cfg', configs)
      this.replyIfRequest(sender, 'mmSuccess')
    }catch(e) {
      this.replyIfRequest(sender, 'mmFailure', { error: 'Error saving configuration: '+e })
    }
  } // mmSaveConfiguration

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Load application configuration from a file and send mmReconfigure to affected listeners
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.id - ID of configuration
  * @param {array} [params.listeners] - limit which listeners to reconfigure
  */
  async mmLoadConfiguration(sender, params) {
    const { id, listeners } = params
    try{
      const configs = mfs.getJSON(id+'.cfg')
      if(!configs || typeof (configs)!=='object') {
        this.replyIfRequest(sender, 'mmFailure', { error: 'Invalid configuration' })
        return
      }
      const promises = []
      let listener_ids = listeners || Object.keys(this._listeners)
      listener_ids = listener_ids
        .filter(id=>!/^\d+$/.test(id))
        .filter(id=>this._listeners[id]?.status==='started')
        .filter(id=>configs[id])
        .sort()
      for(const id of listener_ids) {
        promises.push(this.request(id, 'mmReconfigure', { config: configs[id] }, { timeout: 2000, resolve: 'mmSuccess' }))
      }

      const results = await Promise.allSettled(promises)
      let nom = 0
      const den = listener_ids.length
      for(let i=0; i<listener_ids.length; i++) {
        if(results[i].status==='fulfilled') nom++
      }
      this.replyIfRequest(sender, 'mmSuccess', { nom, den })
    }catch(e) {
      this.replyIfRequest(sender, 'mmFailure', { error: 'Error loading configuration: '+e })
    }
  } // mmLoadConfiguration
} // msysExec
